import React, { useCallback, useEffect, useRef, useState } from 'react';

/*
 * PROD-7828 R8 — debounced server-side lookup (teacher / session). Presentational: the caller owns
 * the request (api.js) and the option rendering; this only debounces, keeps the latest response and
 * reports the picked row. id labels the control (the input, or the Change button once picked); name
 * gives each Change button its own accessible name ("Change teacher", "Change session"). label_hidden
 * keeps the label for screen readers only, as in a filter row of unlabelled selects.
 */
const DEBOUNCE_MS = 300;

// The list's options, in order, for arrow-key moves; they sit outside the Tab order.
const option_nodes = (wrap) => (wrap ? [...wrap.querySelectorAll('.tp-lookup-opt')] : []);

const LookupPicker = ({
  id,
  name,
  label,
  label_hidden = false,
  placeholder,
  min_chars = 2,
  value_text,
  disabled,
  disabled_hint,
  search,
  render_option,
  option_key,
  on_pick,
  on_clear,
  error_text
}) => {
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  // Open only while focus is inside the widget (the input or an option), so it never covers Save.
  const [open, setOpen] = useState(false);
  // Only the latest query may render; a slow early response must not replace a newer list.
  const seq = useRef(0);
  const wrap_ref = useRef(null);
  const input_ref = useRef(null);
  const change_ref = useRef(null);
  // A pick or a Change swaps the focused control for another; this names which one takes focus next.
  const refocus = useRef(null);
  const list_id = `${id}-list`;

  const run = useCallback(
    (q) => {
      seq.current += 1;
      const mine = seq.current;
      setLoading(true);
      setFailed(false);
      search(q)
        .then((rows) => {
          if (mine !== seq.current) return;
          setOptions(Array.isArray(rows) ? rows : []);
          setLoading(false);
        })
        .catch(() => {
          if (mine !== seq.current) return;
          setOptions([]);
          setFailed(true);
          setLoading(false);
        });
    },
    [search]
  );

  // A closed list does not look anything up: a keystroke elsewhere in the form must not reach the server.
  useEffect(() => {
    if (!open || disabled || value_text || query.trim().length < min_chars) {
      setOptions([]);
      return undefined;
    }
    setLoading(true);
    const t = setTimeout(() => run(query.trim()), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [open, query, disabled, value_text, min_chars, run]);

  useEffect(() => {
    if (!open) return undefined;
    const on_down = (e) => {
      if (wrap_ref.current && !wrap_ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', on_down);
    return () => document.removeEventListener('mousedown', on_down);
  }, [open]);

  // After the swap, focus goes to the control that replaced the one it was on (never the page body).
  useEffect(() => {
    const target = refocus.current === 'change' ? change_ref.current : input_ref.current;
    if (refocus.current && target) target.focus();
    refocus.current = null;
  }, [value_text]);

  const shown = open && !disabled && query.trim().length >= min_chars;
  // The listbox holds options only; its states are announced in a status region outside it.
  const has_list = shown && !loading && !failed && options.length > 0;
  let status_text = '';
  if (shown && loading) status_text = 'Searching…';
  else if (shown && failed) status_text = 'The lookup failed — try again.';
  else if (shown && options.length === 0) status_text = 'No matches.';

  // Esc closes the list only; stopping it here keeps an enclosing TpDialog open.
  const on_key = (e) => {
    const nodes = option_nodes(wrap_ref.current);
    const at = nodes.indexOf(document.activeElement);
    if (e.key === 'Escape' && shown) {
      e.stopPropagation();
      setOpen(false);
      if (input_ref.current) input_ref.current.focus();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!shown) setOpen(true);
      else if (nodes.length) nodes[Math.min(at + 1, nodes.length - 1)].focus();
      return;
    }
    if (e.key === 'ArrowUp' && at !== -1) {
      e.preventDefault();
      if (at === 0) input_ref.current.focus();
      else nodes[at - 1].focus();
    }
  };

  if (value_text) {
    return (
      <div className="tp-field">
        <label htmlFor={id} className={label_hidden ? 'sr-only' : undefined}>
          {label}
        </label>
        <div className="tp-picked">
          <span>{value_text}</span>
          <button
            ref={change_ref}
            id={id}
            type="button"
            className="tp-link"
            aria-label={`Change ${name}`}
            onClick={() => {
              setQuery('');
              setOptions([]);
              refocus.current = 'input';
              on_clear();
            }}
          >
            Change
          </button>
        </div>
      </div>
    );
  }

  return (
    // Keyboard handling lives on the input and options; the wrapper only sees their bubbled events.
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      ref={wrap_ref}
      className="tp-field tp-lookup"
      onKeyDown={on_key}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setOpen(false);
      }}
    >
      <label htmlFor={id} className={label_hidden ? 'sr-only' : undefined}>
        {label}
      </label>
      <input
        ref={input_ref}
        id={id}
        className="tp-input"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={has_list}
        aria-controls={has_list ? list_id : undefined}
        placeholder={placeholder}
        value={query}
        disabled={disabled}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        // Focus handed back from an option (Esc, ArrowUp) must not reopen the list.
        onFocus={(e) => {
          if (!wrap_ref.current.contains(e.relatedTarget)) setOpen(true);
        }}
        onClick={() => setOpen(true)}
      />
      {disabled && disabled_hint ? <span className="tp-hint">{disabled_hint}</span> : null}
      {!disabled && query.trim().length > 0 && query.trim().length < min_chars ? (
        <span className="tp-hint">Type at least {min_chars} characters.</span>
      ) : null}
      {error_text ? <span className="tp-err">{error_text}</span> : null}
      <div role="status" className={status_text ? 'tp-lookup-list' : 'sr-only'}>
        {status_text ? <div className="tp-lookup-empty">{status_text}</div> : null}
      </div>
      {has_list ? (
        <div id={list_id} className="tp-lookup-list" role="listbox" aria-label={label}>
          {options.map((o) => (
            <button
              type="button"
              key={option_key(o)}
              className="tp-lookup-opt"
              role="option"
              aria-selected={false}
              tabIndex={-1}
              // Keeps focus in the input, so the blur-close cannot swallow the click.
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                setOpen(false);
                setQuery('');
                refocus.current = 'change';
                on_pick(o);
              }}
            >
              {render_option(o)}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default LookupPicker;
